import { FLAG_ORY_SESSION } from '@/lib/utils/flags'
import { useFlag } from '@unleash/proxy-client-vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import AutoComplete from '../../components/AutoComplete'
import Checkbox from '../../components/Checkbox'
import DropDown from '../../components/DropDown'
import DynamicInput from '../../components/DynamicInput'
import ErrorBlock from '../../components/ErrorBlock'
import FormActions from '../../components/FormActions'
import Input from '../../components/Input'
import Retry from '../../components/Retry'
import UserComments from '../../components/UserComments'
import { caseOf } from '../../lib'

const SpinnerView = () => (
  <div class="spinner-container">
    <div class="spinner" />
  </div>
)
const _updateField = (context, field) => (value) => {
  context.updateField({ field, value })
}

export default {
  components: {
    ErrorBlock,
  },

  setup() {
    const isOrySessionFeatureOn = useFlag(FLAG_ORY_SESSION)
    return {
      isOrySessionFeatureOn,
    }
  },

  computed: {
    ...mapGetters('users', ['user', 'clients', 'updateStatus', 'usersList', 'mergeUsersStatus', 'oneTimeEarnings', 'errorMessage']),
  },

  methods: {
    ...mapActions('users', [
      'init',
      'updateField',
      'changeClient',
      'updateUser',
      'reset',
      'removeOneTimePayment',
      'mergeUsers',
      'updateUsersList',
      'findUsers',
      'getOneTimeEarnings',
      'updateOneTimeEarnings',
      'gdprDelete',
    ]),

    ...mapMutations('users', ['addEmptyOneTimeEarning', 'removeOneTimeEarning', 'updateOneTimeEarning']),

    async submitHandler(e) {
      e.preventDefault()
      const res = await this.updateUser()
      if (res) {
        this.updateOneTimeEarnings()
      }
    },
  },

  created() {
    const { id } = this.$route.params
    this.init(id)
    this.getOneTimeEarnings(id)
  },

  render() {
    const userId = this.$route.params.id

    return (
      <div class="page">
        {caseOf(this.user, {
          Done: (user) => (
            <div class="card" onSubmit={this.submitHandler}>
              <header>
                <h2>
                  Edit user: {userId}
                  <router-link to={`/users/${userId}`}>View user</router-link>
                </h2>
              </header>
              <form>
                <Checkbox checked={user.is_verified} update={_updateField(this, 'is_verified')} label="Approved" />
                <Checkbox checked={user.payment_blocked} update={_updateField(this, 'payment_blocked')} label="Block Payments" />
                <Checkbox checked={user.is_suspended} update={_updateField(this, 'is_suspended')} label="Banned" />
                {user.recruitment_utm && (
                  <div>
                    <label>Recruitment:</label>
                    {user.recruitment_data?.campaign ? (
                      <span>
                        {user.recruitment_data.campaign.advertiser.name}: {user.recruitment_data.campaign.name} (ID:{' '}
                        {user.recruitment_data.campaign.id})
                      </span>
                    ) : (
                      <span>{user.recruitment_utm}</span>
                    )}
                  </div>
                )}
                {user.is_suspended === false ? (
                  ''
                ) : (
                  <Checkbox
                    checked={user.remove_earnings}
                    update={_updateField(this, 'remove_earnings')}
                    label="Remove earning for this user"
                  />
                )}

                <Input
                  id="edit-user-email"
                  label="Email"
                  placeholder="E.g. someone@someplace.com"
                  value={user.email}
                  update={(value) => this.updateField({ field: 'email', value })}
                  disabled={true}
                />
                <DropDown
                  id="edit-user-client"
                  label="Client"
                  value={user.client_id}
                  update={(value) => this.changeClient(value)}
                  items={this.clients.map((client) => {
                    return {
                      label: client.name,
                      value: client.id,
                    }
                  })}
                  default={{ value: 'Choose client', unselectable: true }}
                />
                <DropDown
                  id="edit-user-reveneu-tier"
                  label="Revenue Tier"
                  value={user.revenue_tier_id}
                  update={(value) => this.updateField({ field: 'revenue_tier_id', value })}
                  items={this.clients
                    .find((client) => client.id === Number(user.client_id))
                    .revenue_tiers.map((tier) => ({
                      value: tier.id,
                      label: `${tier.name} - ${parseFloat(tier.revenue_share, 10) * 100}%`,
                    }))}
                  default={{ value: 'Choose revenue tier', unselectable: true }}
                />
                <Input
                  id="edit-user-name"
                  label="Username"
                  placeholder="E.g. awesome-blogger"
                  value={user.username}
                  update={(value) => this.updateField({ field: 'username', value })}
                />
                <DynamicInput
                  id="edit-user-one-time-payment"
                  label="One Time Payments"
                  fields={[
                    {
                      type: 'text',
                      key: 'user_earnings',
                      label: 'User Earning',
                      isDecimal: true,
                      placeholder: 'User earning',
                      default: {
                        value: 0,
                      },
                    },
                    {
                      type: 'text',
                      key: 'invoice_amount',
                      label: 'Invoice amount',
                      isDecimal: true,
                      placeholder: 'Store earning',
                      default: {
                        value: 0,
                      },
                    },
                    {
                      type: 'text',
                      key: 'store_id',
                      label: 'Store id',
                      placeholder: 'Store id',
                      default: {
                        value: 0,
                      },
                    },
                    {
                      type: 'text',
                      key: 'payment_date',
                      label: 'Payment Date',
                      placeholder: '2019-01-01',
                      default: {
                        value: '',
                      },
                    },
                    {
                      type: 'text',
                      key: 'comment',
                      label: 'Comment',
                      placeholder: 'Leave the comment',
                      default: {
                        value: '',
                      },
                    },
                  ]}
                  items={this.oneTimeEarnings}
                  addItem={this.addEmptyOneTimeEarning}
                  updateItem={(index, field, value) => this.updateOneTimeEarning({ index, field, value })}
                  removeItem={(id) => this.removeOneTimeEarning(id)}
                  addLabel="Add payment"
                />

                <FormActions
                  html={
                    <button
                      type="button"
                      onClick={() => {
                        this.gdprDelete()
                      }}
                    >
                      GDPR delete
                    </button>
                  }
                  status={this.updateStatus}
                  reset={() => this.reset()}
                  messages={{
                    saved: 'Client was successfully saved!',
                    updated: 'Client was successfully updated!',
                    error: this.errorMessage,
                  }}
                />
              </form>

              <h4>Merging users:</h4>
              <AutoComplete
                id="select-user"
                kind={'usersList'}
                update={(value) => {
                  this.updateUsersList({ path: 'value', value })
                  this.findUsers(value)
                }}
                suggestions={this.usersList.suggestions}
                itemSelected={(value) => this.updateUsersList({ path: 'selected', value: value })}
                value={this.usersList.value}
                clearOnSelection={false}
                label="Select User to merge into this"
              />

              {Object.keys(this.usersList.selected).length === 0 ? (
                ''
              ) : (
                <div class="merge-user">
                  {caseOf(this.mergeUsersStatus, {
                    Pending: () => (
                      <div class="spinner-container">
                        <div class="spinner" />
                      </div>
                    ),
                    Done: (m) => (m ? <div class="success">{m}</div> : ''),
                    Failed: () => <ErrorBlock error={this.errorMessage} />,
                    _: () => null,
                  })}
                  <label>
                    Merge {this.usersList.selected.email}({this.usersList.selected.id}) into this user ({user.id}). User{' '}
                    {this.usersList.selected.id} will be removed after merging{' '}
                  </label>
                  <button
                    class="success"
                    onClick={() => {
                      this.mergeUsers()
                    }}
                  >
                    Merge
                  </button>
                </div>
              )}

              <UserComments />
            </div>
          ),
          Error: () => <Retry refresh={() => this.init(userId)} />,
          _: () => <SpinnerView />,
        })}
      </div>
    )
  },
}
